// SAD-861: the base and option-period lines a team owns. CCSS's FMS and Open
// Systems team performs most of CLIN 0001's deliverables while 0001AA–AD stay
// contract-wide: a team picks the base sub-lines it owns, and by default the
// same lines in every option period. Optional-task lines are still linked by
// their exercise (Link exercise).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraCheckboxField, SpecraFormField, SpecraInlineBanner, SpecraTag, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useUpdateWorkstream } from '../hooks';
import { mintReceipt } from '../lib/rule';
import { CmDrawer } from './CmDrawer';
/** A line's name: its short title, else the first line of its description. */ function lineTitle(c) {
    return (c.shortTitle || c.description.split('\n')[0] || '').trim();
}
const isBase = (c)=>c.optionType.toLowerCase() === 'base';
const isOption = (c)=>c.optionType.toLowerCase() === 'option_period';
/** The base period's lines, each with its sub-lines, in schedule order. */ export function baseLineTree(clins) {
    const base = clins.filter(isBase);
    const out = [];
    const byNumber = new Map();
    for (const c of base){
        if (c.parentClinNumber) continue;
        if (byNumber.has(c.clinNumber)) continue;
        const l = {
            number: c.clinNumber,
            title: lineTitle(c),
            subLines: []
        };
        byNumber.set(c.clinNumber, l);
        out.push(l);
    }
    for (const c of base){
        const parent = c.parentClinNumber && byNumber.get(c.parentClinNumber);
        if (parent && !parent.subLines.some((s)=>s.number === c.clinNumber)) parent.subLines.push({
            number: c.clinNumber,
            title: lineTitle(c)
        });
    }
    return out;
}
/**
 * The same lines in every option period: "0001AE" is "1001AE", "2001AE", …
 * for each option period that prices "n001". The family is the line's number
 * after its period digit.
 */ export function optionCounterparts(baseNumbers, clins) {
    const parents = clins.filter((c)=>isOption(c) && !c.parentClinNumber).map((c)=>c.clinNumber);
    const out = [];
    for (const n of baseNumbers){
        const digits = n.replace(/[A-Za-z]+$/, '');
        const suffix = n.slice(digits.length);
        for (const p of parents){
            if (p.length === digits.length && p.slice(1) === digits.slice(1) && p !== digits) {
                const line = p + suffix;
                if (!out.includes(line)) out.push(line);
            }
        }
    }
    return out.sort();
}
/**
 * The base picks and the "every option period" choice a stored set reads as.
 * `kept` are stored lines the picks do not produce (an option-period line
 * linked on its own): the drawer keeps them, never drops them on save.
 */ export function readTeamLines(lines, clins) {
    const baseNumbers = new Set(clins.filter(isBase).map((c)=>c.clinNumber));
    const base = lines.filter((n)=>baseNumbers.has(n));
    const others = lines.filter((n)=>!baseNumbers.has(n));
    const derived = optionCounterparts(base, clins);
    // "Every option period" only when every counterpart is stored; a partial
    // set (1001AE without 2001AE) is kept as it is, never widened on save.
    const all = derived.length > 0 && derived.every((n)=>others.includes(n));
    const kept = all ? others.filter((n)=>!derived.includes(n)) : others;
    return {
        base,
        everyPeriod: base.length === 0 || all,
        kept
    };
}
/** The numbers a team owns, from its base picks (and any kept lines). */ export function teamLineNumbers(base, everyPeriod, clins, kept = []) {
    return [
        ...new Set([
            ...base,
            ...everyPeriod ? optionCounterparts(base, clins) : [],
            ...kept
        ])
    ].sort();
}
/** "0001AE–0001AX": a run of consecutive sub-lines of one line reads as one. */ export function linesSummary(lines) {
    const sorted = [
        ...lines
    ].sort();
    if (sorted.length <= 3) return sorted.join(', ');
    return `${sorted[0]}–${sorted[sorted.length - 1]} (${sorted.length} lines)`;
}
/** The controlled picker: base lines with their sub-lines, and the option-period choice. */ export function TeamLinesPicker({ programId, base, everyPeriod, onChange, testId }) {
    const clins = useContractCLINs(programId);
    const tree = useMemo(()=>baseLineTree(clins.data?.clins ?? []), [
        clins.data
    ]);
    const picked = new Set(base);
    const set = (numbers, on)=>{
        const next = new Set(picked);
        for (const n of numbers){
            if (on) next.add(n);
            else next.delete(n);
        }
        onChange([
            ...next
        ].sort(), everyPeriod);
    };
    const counterparts = optionCounterparts(base, clins.data?.clins ?? []);
    if (clins.isLoading) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            as: "p",
            size: "caption",
            tone: "muted",
            children: "Reading the contract’s lines…"
        });
    }
    if (tree.length === 0) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            as: "p",
            size: "caption",
            tone: "muted",
            "data-testid": testId,
            children: "The contract states no base-period lines yet. Teams on optional tasks are linked by their exercise instead."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "max-h-80 space-y-3 overflow-y-auto pr-1",
                children: tree.map((line)=>{
                    const own = line.subLines.length > 0 ? line.subLines.map((s)=>s.number) : [
                        line.number
                    ];
                    const n = own.filter((x)=>picked.has(x)).length;
                    return /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.cmWorkstream.lineGroup,
                        "data-row-id": line.number,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                label: /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium",
                                    children: [
                                        line.number,
                                        line.title ? /*#__PURE__*/ _jsxs("span", {
                                            className: "font-normal text-muted-foreground",
                                            children: [
                                                " · ",
                                                line.title
                                            ]
                                        }) : null
                                    ]
                                }),
                                description: line.subLines.length > 0 ? `${n} of its ${line.subLines.length} deliverable lines` : undefined,
                                controlProps: {
                                    checked: n === own.length ? true : n > 0 ? 'indeterminate' : false,
                                    onCheckedChange: (v)=>set(own, v === true),
                                    'aria-label': `Every deliverable line of ${line.number}`,
                                    ...{
                                        'data-testid': Testids.cmWorkstream.lineOption,
                                        'data-row-id': line.number
                                    }
                                }
                            }),
                            line.subLines.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                                className: "mt-1 ml-6 space-y-1",
                                children: line.subLines.map((s)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                            label: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "tabular-nums",
                                                        children: s.number
                                                    }),
                                                    s.title ? /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-muted-foreground",
                                                        children: [
                                                            " · ",
                                                            s.title
                                                        ]
                                                    }) : null
                                                ]
                                            }),
                                            controlProps: {
                                                checked: picked.has(s.number),
                                                onCheckedChange: (v)=>set([
                                                        s.number
                                                    ], v === true),
                                                'aria-label': `${s.number} ${s.title}`,
                                                ...{
                                                    'data-testid': Testids.cmWorkstream.lineOption,
                                                    'data-row-id': s.number
                                                }
                                            }
                                        })
                                    }, s.number))
                            }) : null
                        ]
                    }, line.number);
                })
            }),
            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                label: "The same lines in every option period",
                description: counterparts.length > 0 ? `Also ${counterparts.slice(0, 3).join(', ')}${counterparts.length > 3 ? ` and ${counterparts.length - 3} more` : ''}.` : 'Each option period’s matching lines, once you pick some.',
                controlProps: {
                    checked: everyPeriod,
                    onCheckedChange: (v)=>onChange(base, v === true)
                }
            })
        ]
    });
}
/** The team page's drawer: change the lines a team owns. */ export function TeamLinesDrawer({ programId, workstreamId, lines, onClose }) {
    const clins = useContractCLINs(programId);
    const all = clins.data?.clins ?? [];
    const initial = readTeamLines(lines, all);
    const [draft, setDraft] = useState(null);
    const value = draft ?? initial;
    const kept = initial.kept;
    const save = useUpdateWorkstream(programId, {
        errors: 'inline',
        success: (r)=>`Lines saved. ${mintReceipt(r.result?.mint)}`
    });
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Lines this team owns",
        description: "The deliverables of these lines are this team’s. Lines no team owns stay contract-wide.",
        onClose: onClose,
        busy: save.isPending,
        testId: Testids.cmWorkstream.linkLinesDrawer,
        footerProps: {
            confirmLabel: 'Save lines',
            onConfirm: ()=>save.mutate({
                    workstreamId,
                    replaceLines: true,
                    lineNumbers: teamLineNumbers(value.base, value.everyPeriod, all, kept)
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: save.isPending || clins.isLoading,
            loading: save.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmWorkstream.linkLinesSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "info",
                    variant: "framed",
                    children: "Saving moves these lines’ dates to the team and re-schedules them. A date already submitted or waived keeps its team."
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Base-period lines",
                    children: /*#__PURE__*/ _jsx(TeamLinesPicker, {
                        programId: programId,
                        base: value.base,
                        everyPeriod: value.everyPeriod,
                        onChange: (base, everyPeriod)=>setDraft({
                                base,
                                everyPeriod
                            })
                    })
                }),
                kept.length > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: [
                        "Also owns ",
                        kept.join(', '),
                        ", linked on its own; saving keeps them."
                    ]
                }) : null,
                save.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(save.error, 'Could not save the lines')
                }) : null
            ]
        })
    });
}
/**
 * The header's "Lines:" reading: the base lines, a run read as one
 * ("0001AE–0001AX (18 lines)"), and how many option periods repeat them.
 */ export function teamLinesReading(lines) {
    const base = lines.filter((l)=>l.startsWith('0'));
    // An option period's line leads with its number (1001AE is option period 1).
    const periods = new Set(lines.filter((l)=>!l.startsWith('0')).map((l)=>l[0]));
    return {
        base: linesSummary(base.length > 0 ? base : lines),
        periods: base.length > 0 ? periods.size : 0
    };
}
/** The header's "Lines:" chip and its option-period note. */ export function TeamLinesTags({ lines }) {
    if (lines.length === 0) return null;
    const r = teamLinesReading(lines);
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-1.5",
        "data-testid": Testids.cmWorkstream.lines,
        children: [
            /*#__PURE__*/ _jsx(SpecraTag, {
                children: r.base
            }),
            r.periods > 0 ? /*#__PURE__*/ _jsxs("span", {
                className: "text-muted-foreground",
                children: [
                    "and the same in ",
                    r.periods,
                    " option ",
                    r.periods === 1 ? 'period' : 'periods'
                ]
            }) : null
        ]
    });
}
